import { useBookingV2 } from "context/BookingV2Context"
import { useCartDispatch, useCartState } from "hooks/useCartV2"
import { useLang } from "hooks/useLang"
import { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { SelectedSeat, SelectedTickets } from "types/Booking"
import SAR from "components/Sar/SAR"
import { SeatsioSeatingChart } from "@seatsio/seatsio-react"
import { isEidPartyEvent as checkIsEidPartyEvent } from  "utils/helpers/constants"

export interface ChartInstance {
    selectedObjects: string[]
    selectedSeats: string[]
    deselectObjects: (objectIds: string[]) => void
}
export interface ChartCategory {
    label: string
    key: number
}
export interface ChartMainObject {
    category: ChartCategory
    id: string
    chart: ChartInstance
    uuid: string
    seatId: string
}

const SelectSeatV2 = () => {
    const { event } = useBookingV2()
    const cartState = useCartState()
    const cartDispatch = useCartDispatch()
    const cartContext = useCartState()
    const { t } = useLang()
    const [chart, setChart] = useState({})
    const [countdown, setCountdown] = useState(0)
    const chartRef = useRef<any>(null)

    const isEidPartyEvent = checkIsEidPartyEvent(event?._id)

    const selectedTypes = cartState.selectedDays?.types || []

    // Get all available categories from the selected timeslot
    const availableCategories = useMemo(() => {
        return selectedTypes.map((type) => type.en_title)
    }, [selectedTypes])

    const maxSelectionAllowed = useMemo(() => {
        return undefined
    }, [])

    // Group selected seats by category
    const groupedSelectedSeats = useMemo(() => {
        const grouped = cartContext.selectedSeats.reduce((acc, seat) => {
            if (!acc[seat.label]) {
                acc[seat.label] = []
            }
            acc[seat.label].push(seat)
            return acc
        }, {})

        return Object.entries(grouped).map(([category, seats]) => {
            const matchingType = selectedTypes.find(
                (type) => type.en_title === category
            )

            const price = matchingType ? Number(matchingType.price) : 0

            return {
                category,
                seats: seats as SelectedSeat[],
                count: (seats as SelectedSeat[]).length,
                price: price,
            }
        })
    }, [cartContext.selectedSeats, selectedTypes])

    const totalSelectedSeats = useMemo(() => {
        return cartContext.selectedSeats.length
    }, [cartContext.selectedSeats])

    const findTicketByCategory = useCallback(
        (categoryLabel: string) => {
            if (!categoryLabel) {
                console.error("Empty category label provided")
                return null
            }

            const selectedDay = cartState.selectedDays
            if (!selectedDay?.types?.length) {
                console.error("No types available in selected day")
                return null
            }

            const matchingType = selectedDay.types.find(
                (type) => type.en_title === categoryLabel
            )

            if (!matchingType) {
                console.warn(
                    `No matching ticket type found for category: ${categoryLabel}`
                )
                return null
            }

            const ticketPrice = matchingType.price
                ? Number(matchingType.price)
                : 0

            const ticket = {
                type_id: matchingType.type_id,
                timeslot_id: selectedDay.timeslot_id,
                start: selectedDay.start,
                end: selectedDay.end,
                ar_title: matchingType.ar_title || "",
                en_title: matchingType.en_title || "",
                ar_description: matchingType.ar_description || "",
                en_description: matchingType.en_description || "",
                price: ticketPrice,
                limit: matchingType.limit || 0,
                min: matchingType.min || 0,
                max: matchingType.max || 0,
                color: matchingType.color || "#201750",
                must_select_seat: true,
                group: matchingType.group || "",
                promotion: matchingType.promotion || undefined,
            }

            return ticket
        },
        [cartState.selectedDays]
    )

    // Update tickets based on seats (only for EID events)
    const updateTicketsBasedOnSeats = useCallback(
        (seats: SelectedSeat[]) => {
            if (!isEidPartyEvent) return // Only apply for EID events

            // Validate seats have valid labels
            const validSeats = seats.filter((seat) => seat && seat.label)

            if (validSeats.length === 0) {
                console.warn("No valid seats with labels found")
                return
            }

            // Clear existing tickets
            cartDispatch({
                type: "REMOVE_TICKETS",
            })

            // For each valid seat, create an individual ticket
            validSeats.forEach((seat) => {
                const categoryLabel = seat.label || "Unknown"

                if (categoryLabel === "Unknown") {
                    console.warn("Skipping unknown category seat")
                    return
                }

                const ticket = findTicketByCategory(categoryLabel)
                if (ticket) {
                    // Create a new ticket with this specific seat ID
                    const seatTicket = {
                        ...ticket,
                        seat_id: seat.id, // Use the individual seat ID like "GA Zone B-368"
                    }

                    cartDispatch({
                        type: "ADD_TICKET",
                        payload: seatTicket,
                    })
                } else {
                    console.warn(
                        `No matching ticket found for category: ${categoryLabel}`
                    )
                }
            })
        },
        [isEidPartyEvent, cartDispatch, findTicketByCategory]
    )

    const pricing = useMemo(() => {
        return selectedTypes.map((type) => ({
            category: type.en_title,
            price: type.price ? Number(type.price) : 0,
        }))
    }, [selectedTypes])

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
            return () => clearTimeout(timer)
        }
    }, [countdown])

    // Remove seat from both state and chart
    const removeSeat = (seat) => {
        // Remove seat from state
        cartDispatch({
            type: "REMOVE_SEAT",
            payload: seat,
        })

        // Remove seat from chart if chartRef is available
        if (chartRef.current && chartRef.current.deselectObjects) {
            chartRef.current.deselectObjects([seat.id])
        }

        // For EID events, update tickets after removing seat
        if (isEidPartyEvent) {
            const updatedSeats = cartContext.selectedSeats.filter(
                (s) => s.id !== seat.id
            )
            updateTicketsBasedOnSeats(updatedSeats)
        }
    }

    if (
        !event ||
        !event.seatsio.season_id ||
        !cartState.selectedDays?.seats_season_id
    ) {
        return null
    }

    // Render the selected seats side panel
    const renderSelectedSeatsSidePanel = () => {
        // Calculate total price across all categories
        const totalPrice = groupedSelectedSeats.reduce(
            (sum, group) => sum + group.price * group.count,
            0
        )

        return (
            <div className="flex flex-col bg-white rounded-lg shadow-md p-4 overflow-auto md:max-h-[calc(100vh-210px)] max-h-[320px]">
                <h3 className="text-[#201750] font-bold text-lg mb-4 border-b pb-2 sticky top-0 bg-white z-10">
                    {t("selected_seats")} ({totalSelectedSeats})
                </h3>

                {groupedSelectedSeats.length === 0 ? (
                    <div className="text-gray-500 text-center py-4">
                        {t("no_seats_selected")}
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col space-y-3">
                            {groupedSelectedSeats.map((group) => (
                                <div
                                    key={group.category}
                                    className="bg-gray-50 p-3 rounded-md"
                                >
                                    <div className="flex justify-between items-center mb-2">
                                        <h4 className="font-semibold text-[#201750]">
                                            {group.category}
                                        </h4>
                                        <span className="text-gray-600 text-sm">
                                            {group.count}{" "}
                                            {group.count > 1
                                                ? t("seats")
                                                : t("seat")}
                                        </span>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        {group.seats.map((seat) => (
                                            <div
                                                key={seat.id}
                                                className="flex justify-between items-center"
                                            >
                                                <span className="text-sm text-gray-700">
                                                    {seat.id.replace(
                                                        /[^\w\s]/gi,
                                                        ""
                                                    )}
                                                </span>
                                                <button
                                                    className="text-red-500 text-xs hover:text-red-700"
                                                    onClick={() =>
                                                        removeSeat(seat)
                                                    }
                                                >
                                                    {t("remove")}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-2 pt-2 border-t flex justify-between">
                                        <span className="text-gray-600 text-sm">
                                            {t("subtotal")}:
                                        </span>
                                        <span className="text-[#201750] font-semibold">
                                            <SAR
                                                color="#201750"
                                                price={Number(
                                                    (
                                                        group.price *
                                                        group.count
                                                    ).toFixed(2)
                                                )}
                                            />
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Total Price */}
                        <div className="mt-4 pt-3 border-t border-gray-300 sticky bottom-0 bg-white">
                            <div className="flex justify-between items-center">
                                <span className="text-[#201750] font-bold">
                                    {t("total")}:
                                </span>
                                <span className="text-[#201750] font-bold text-lg">
                                    <SAR
                                        color="#201750"
                                        price={Number(totalPrice.toFixed(2))}
                                    />
                                </span>
                            </div>
                        </div>
                    </>
                )}
            </div>
        )
    }

    return (
        <div className="relative container flex flex-col md:flex-row gap-5 items-start justify-center overflow-hidden">
            <div className="w-full md:w-2/3 mb-4 md:mb-0">
                <div className="w-full">
                    <SeatsioSeatingChart
                        fitTo="width"
                        availableCategories={
                            isEidPartyEvent ? undefined : availableCategories
                        }
                        selectedObjects={cartContext.selectedSeats.map(
                            (seat) => seat.id
                        )}
                        // priceFormatter={formatPrice}
                        pricing={pricing}
                        showSectionPricingOverlay={true}
                        // showLegend={window.innerWidth > 768}
                        showFullScreenButton={false}
                        channels={
                            cartState.selectedDays?.seat_channels?.user_channel
                                ? [
                                      cartState.selectedDays.seat_channels
                                          .user_channel,
                                  ]
                                : undefined
                        }
                        region="eu"
                        language="ar"
                        workspaceKey={
                            process.env.NEXT_PUBLIC_SEATS_WORKSPACE_KEY
                        }
                        event={cartContext?.selectedDays?.seats_season_id}
                        onChartRendered={(chartObj) => {
                            chartRef.current = chartObj
                        }}
                        onObjectSelected={(o: any) => {
                            // Check if o exists
                            if (!o) {
                                console.error("Selected object is undefined")
                                return
                            }

                            // Extract section title to match with ticket en_title
                            // The MOST important property path is labels.section
                            let categoryLabel = o.labels.section

                            // Try to get the label from different potential properties, in order of priority

                            const newSeat = {
                                id: o.id || "",
                                label: categoryLabel,
                            }

                            cartDispatch({
                                type: "ADD_SEATS",
                                payload: newSeat,
                            })

                            // For EID events, update tickets based on the new seat selection
                            if (isEidPartyEvent) {
                                // Need to update with the newly added seat
                                const updatedSeats = [
                                    ...cartContext.selectedSeats,
                                    newSeat,
                                ]
                                updateTicketsBasedOnSeats(updatedSeats)
                            }
                        }}
                        //   @ts-ignore
                        onObjectDeselected={(o: ChartMainObject) => {
                            // Check if o exists
                            if (!o) {
                                console.error("Deselected object is undefined")
                                return
                            }

                            // Extract section title to match with ticket en_title
                            // The MOST important property path is labels.section
                            let categoryLabel = "Unknown"

                            // Try to get the label from different potential properties, in order of priority
                            if (
                                (o as any).labels &&
                                (o as any).labels.section
                            ) {
                                categoryLabel = (o as any).labels.section
                            } else if (o.category?.label) {
                                categoryLabel = o.category.label
                            } else if ((o.category as any)?.name) {
                                categoryLabel = (o.category as any).name
                            } else if ((o as any).section?.label) {
                                categoryLabel = (o as any).section.label
                            } else if ((o as any).section) {
                                categoryLabel = (o as any).section
                            }

                            const removedSeat = {
                                id: o.id || "",
                                label: categoryLabel,
                            }

                            cartDispatch({
                                type: "REMOVE_SEAT",
                                payload: removedSeat,
                            })

                            // For EID events, update tickets based on remaining seats
                            if (isEidPartyEvent) {
                                // Need to update with the seat removed
                                const updatedSeats =
                                    cartContext.selectedSeats.filter(
                                        (seat) => seat.id !== removedSeat.id
                                    )
                                updateTicketsBasedOnSeats(updatedSeats)
                            }
                        }}
                        onSelectedObjectBooked={(o) => {
                            setChart({ ...chart, ...o })
                        }}
                        maxSelectedObjects={maxSelectionAllowed}
                        // objectCategories={objectCategories}
                        // showRowLabels={true}
                        // showRowLines={true}
                        showSectionLabels={true}
                        colorScheme="light"
                        // objectColor={(section, defaultColor) => {
                        //     if (
                        //         section.label === "Silver (Zone A)" ||
                        //         section.label === "Silver (Zone B)" ||
                        //         section.label === "Silver (Zone C)"
                        //     ) {
                        //         return "#CDCDCD"
                        //     }
                        //     if (
                        //         section.label === "Gold (Zone A)" ||
                        //         section.label === "Gold (Zone B)" ||
                        //         section.label === "Gold (Zone C)"
                        //     ) {
                        //         return "#FFD700"
                        //     }
                        //     if (
                        //         section.label === "VIP (Zone A)" ||
                        //         section.label === "VIP (Zone B)"
                        //     ) {
                        //         return "#201750"
                        //     }
                        //     if (
                        //         section.label === "Bronze (Zone A)" ||
                        //         section.label === "Bronze (Zone B)" ||
                        //         section.label === "Bronze (Zone C)"
                        //     ) {
                        //         return "#CD7F32"
                        //     }
                        //     if (
                        //         section.label === "GA (Zone A)" ||
                        //         section.label === "GA (Zone B)" ||
                        //         section.label === "GA (Zone C)"
                        //     ) {
                        //         return "#FFFFFF"
                        //     }

                        //     return defaultColor
                        // }}
                        // sectionColor={(section, defaultColor) => {
                        //     if (
                        //         section.label === "Silver (Zone A)" ||
                        //         section.label === "Silver (Zone B)" ||
                        //         section.label === "Silver (Zone C)"
                        //     ) {
                        //         return "#CDCDCD"
                        //     }
                        //     if (
                        //         section.label === "Gold (Zone A)" ||
                        //         section.label === "Gold (Zone B)" ||
                        //         section.label === "Gold (Zone C)"
                        //     ) {
                        //         return "#FFD700"
                        //     }
                        //     if (
                        //         section.label === "VIP (Zone A)" ||
                        //         section.label === "VIP (Zone B)"
                        //     ) {
                        //         return "#201750"
                        //     }
                        //     if (
                        //         section.label === "Bronze (Zone A)" ||
                        //         section.label === "Bronze (Zone B)" ||
                        //         section.label === "Bronze (Zone C)"
                        //     ) {
                        //         return "#CD7F32"
                        //     }
                        //     if (
                        //         section.label === "GA (Zone A)" ||
                        //         section.label === "GA (Zone B)" ||
                        //         section.label === "GA (Zone C)"
                        //     ) {
                        //         return "#FFFFFF"
                        //     }

                        //     return defaultColor
                        // }}
                    />
                </div>
            </div>

            {/* Selected Seats Side Panel */}
            <div className="w-full md:w-1/3 md:sticky md:top-4">
                {renderSelectedSeatsSidePanel()}
            </div>
        </div>
    )
}

export default SelectSeatV2
